import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { ArrowRight, MapPin, Film, GraduationCap } from 'lucide-react';
import Header from '../components/Header';
import { SEO, buildWebsiteSchema } from '../components/SEO';
import { Footer } from '../components/landing/Footer';
import { useFunnelPageview } from '../lib/funnels';
import { RegionalDirectoryFooter } from '../components/landing/RegionalDirectoryFooter';
import { CategoryBentoGrid } from '../components/landing/CategoryBentoGrid';
import { EditorialFeatureList } from '../components/landing/EditorialFeatureList';
import { EditorialPricing } from '../components/landing/EditorialPricing';
import { EditorialLetter } from '../components/landing/EditorialLetter';
import { EditorialByTheNumbers } from '../components/landing/EditorialByTheNumbers';
import CityPinTicker from '../components/landing/CityPinTicker';

// Curated editorial cover image for the hero. Same nails portrait that
// anchors the bento's Vol. V tile, so the homepage feels visually
// stitched together: hero cover -> bento opener.
const HERO_COVER_IMAGE = '/tutorial-avatars/naomi-nails.jpg';

// Hero ticker messages. The pill cycles through these at a steady beat
// so a single pill carries both the UK-wide onboarding announcement and
// the Sheffield Co-Op launch without crowding the hero with two badges.
const HERO_TICKER_MESSAGES = [
  { icon: 'pin', text: 'Now onboarding across the UK' },
  { icon: 'cap', text: 'Sheffield Student Co-Op tier is now live' },
];
const HERO_TICKER_INTERVAL_MS = 4200;

function HeroTickerPill() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % HERO_TICKER_MESSAGES.length),
      HERO_TICKER_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, []);
  const msg = HERO_TICKER_MESSAGES[index];
  const Icon = msg.icon === 'cap' ? GraduationCap : MapPin;
  return (
    <span
      className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#1A1A1A] text-[#D4AF37] text-[11px] tracking-[0.2em] uppercase font-semibold mb-8 transition-all duration-500"
      data-testid="hero-overline"
      data-ticker-index={index}
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D4AF37]" />
      </span>
      <Icon className="h-3.5 w-3.5" />
      <span key={index} className="animate-fade-in-up">{msg.text}</span>
    </span>
  );
}

/**
 * HeroCover, editorial magazine cover for the hero spread.
 *
 * Renders a curated still image with masthead overlay and a clickable
 * cover line that routes into /feed. Replaced the previous rotating
 * video preview, the live feed data was thin (one provider on loop)
 * and the editorial direction calls for an intentional, hand-picked
 * cover rather than auto-pulled video.
 */
function HeroCover({ image }) {
  return (
    <Link
      to="/feed"
      className="relative block w-full max-w-[460px] mx-auto group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
      data-testid="hero-cover"
      aria-label="Open Feed"
    >
      <div className="relative w-full aspect-[4/5] bg-stone-900 overflow-hidden border border-stone-800/40 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]">
        <img
          src={image}
          alt="Featured nail artist at work in studio"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />

        {/* Top gradient for masthead legibility */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/65 via-black/20 to-transparent pointer-events-none" />

        {/* Editorial cover lines, masthead-style overlay */}
        <div className="absolute top-0 inset-x-0 p-5 lg:p-6 flex items-start justify-between text-white pointer-events-none">
          <div>
            <p className="font-heading text-xs tracking-[0.35em] uppercase text-white">
              Zuriya
            </p>
            <p className="font-mono text-[10px] tracking-widest text-white/75 mt-1">
              Vol. 01 / UK Edition
            </p>
          </div>
          <span className="font-mono text-[10px] tracking-widest text-white/85 px-2 py-1 border border-white/40 backdrop-blur-sm">
            Issue 26
          </span>
        </div>

        {/* Bottom gradient for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/90 via-black/35 to-transparent pointer-events-none" />

        {/* Cover line at the bottom, single message */}
        <div className="absolute bottom-0 inset-x-0 p-5 lg:p-6 text-white">
          <p className="font-heading text-3xl lg:text-4xl leading-tight tracking-tight">
            Live from the Feed
          </p>
          <p className="font-mono text-[10px] tracking-widest uppercase text-white/70 mt-2 group-hover:text-[#D4AF37] transition-colors">
            Watch more &rarr;
          </p>
        </div>
      </div>
    </Link>
  );
}

export default function Landing() {
  const { user } = useAuth();
  // Funnel analytics: tag main Landing visits as `direct` so the admin
  // dashboard can compare against vertical-funnel pages like /for-barbers.
  useFunnelPageview({ path: '/', pillar: null, source: 'direct' });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title={null}
        description="Book trusted inclusive multi-cultural hair, nails, lashes and beauty stylists across the UK. Live availability, SMS reminders, guest checkout, and secure Stripe payments."
        jsonLd={buildWebsiteSchema()}
      />
      <Header />

      {/* Editorial Hero: asymmetric magazine spread on cream */}
      <section
        className="relative bg-[#F9F6F0] paper-grain text-[#1A1A1A] border-b border-stone-200 pt-24 lg:pt-28 pb-16 lg:pb-24"
        data-testid="hero-section"
      >
        {/* Decorative side rule, classic editorial hairline */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute left-12 top-24 bottom-24 w-px bg-stone-300"
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left column, the editorial text spread */}
          <div className="lg:col-span-7 max-w-2xl">
            {/* Issue line */}
            <div className="flex items-center gap-4 mb-8 font-mono text-[11px] tracking-[0.3em] uppercase text-stone-500">
              <span>Vol. 01</span>
              <span aria-hidden="true" className="block w-8 h-px bg-stone-400" />
              <span>UK Edition</span>
              <span aria-hidden="true" className="block w-8 h-px bg-stone-400" />
              <span>Spring 2026</span>
            </div>

            {/* Alternating live ticker pill */}
            <HeroTickerPill />

            {/* The headline */}
            <h1
              className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] tracking-tight leading-[0.95] font-medium text-[#1A1A1A]"
              data-testid="hero-title"
            >
              Beauty <span className="italic text-stone-500">that understands</span> you.
              <span className="block mt-2 text-[#1A1A1A]">
                Talent that <span className="italic text-[#D4AF37]">deserves to be seen.</span>
              </span>
            </h1>

            {/* Editorial deck */}
            <p className="text-lg lg:text-xl text-stone-600 mt-8 leading-relaxed max-w-xl">
              Zuriya is the editorial marketplace for inclusive multi-cultural beauty in the UK. Browse premium professional availability, or unlock our exclusive Student Skill-Exchange Co-Op.
            </p>

            {/* Primary CTAs: Pro marketplace + Student Co-Op */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-10 items-stretch sm:items-start">
              <Link to="/discover" className="w-full sm:w-auto">
                <Button
                  className="w-full sm:w-auto h-14 px-8 text-base rounded-none bg-[#1A1A1A] text-white hover:bg-[#2a2a2a] font-medium tracking-wide"
                  data-testid="hero-cta-browse-pros"
                >
                  Browse Pro Stylists
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to={user ? '/settings' : '/register'} className="w-full sm:w-auto">
                <div
                  className="group relative w-full sm:w-auto"
                  data-testid="hero-cta-coop-wrap"
                >
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto h-14 px-8 text-base rounded-none border-2 border-[#D4AF37] bg-[#FAF8F2] text-[#1A1A1A] hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors font-medium tracking-wide flex items-center gap-2"
                    data-testid="hero-cta-coop"
                  >
                    <GraduationCap className="h-5 w-5 text-[#8a7320] group-hover:text-[#1A1A1A] transition-colors" />
                    Unlock Student Co-Op
                  </Button>
                  <span
                    className="block mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500"
                    data-testid="hero-cta-coop-sublabel"
                  >
                    Requires verified .ac.uk email
                  </span>
                </div>
              </Link>
            </div>

            {/* Tertiary links: provider funnel + Feed */}
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
              {(!user || user.role !== 'provider') && (
                <Link
                  to={user ? '/provider/onboarding' : '/register?role=provider'}
                  className="group inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#1A1A1A] transition-colors"
                  data-testid="become-provider-btn"
                >
                  <span className="relative">
                    Join as a stylist
                    <span
                      aria-hidden="true"
                      className="absolute left-0 -bottom-0.5 h-px w-full bg-[#1A1A1A] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                    />
                  </span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
              <Link
                to="/feed"
                className="group inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#1A1A1A] transition-colors"
                data-testid="hero-feed-link"
              >
                <Film className="h-4 w-4 text-[#D4AF37]" />
                <span className="relative">
                  Or just browse the Feed
                  <span
                    aria-hidden="true"
                    className="absolute left-0 -bottom-0.5 h-px w-full bg-[#D4AF37] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                  />
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Right column, the editorial cover */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Caption above the cover */}
              <div className="hidden lg:flex items-center justify-between mb-4 font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500">
                <span>Cover</span>
                <span>Featured this issue</span>
              </div>
              <HeroCover image={HERO_COVER_IMAGE} />
              {/* Caption below */}
              <div className="mt-4 flex items-start justify-between gap-4 text-xs text-stone-500">
                <p className="font-heading italic text-base text-[#1A1A1A] leading-snug">
                  &ldquo;Every booking is a story. We just made it easier to find the right author.&rdquo;
                </p>
                <span className="font-mono text-[10px] tracking-widest uppercase pt-1 whitespace-nowrap">
                  Pg. 01
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UK-wide positioning band, immediately after the hero */}
      <CityPinTicker />

      {/* Categories, editorial bento */}
      <CategoryBentoGrid />

      {/* Letter from the editor, full-bleed intermission */}
      <EditorialLetter />

      {/* Features, table-of-contents style */}
      <EditorialFeatureList />

      {/* By the Numbers, cinematic full-bleed beat */}
      <EditorialByTheNumbers />

      {/* Pricing, the rate card */}
      <EditorialPricing />

      {/* Provider CTA Section, the cinematic closer */}
      <section className="relative bg-[#1A1A1A] py-24 lg:py-32 overflow-hidden">
        {/* Faint editorial gridlines */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div className="h-full w-full bg-[linear-gradient(to_right,_white_1px,_transparent_1px),_linear-gradient(to_bottom,_white_1px,_transparent_1px)] bg-[size:80px_80px]" />
        </div>
        {/* Gold halo */}
        <div
          aria-hidden="true"
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none"
        />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="overline">Provider Call</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-white mt-4">
            Are you a beauty professional?
          </h2>
          <p className="text-stone-400 mt-6 max-w-2xl mx-auto text-base lg:text-lg leading-relaxed">
            Join Zuriya and grow your client base across the UK. Set your own prices, manage your diary, and get paid the day a client books.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/register?role=provider">
              <Button
                className="h-14 px-8 text-base rounded-none bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#e8c547] font-medium tracking-wide"
                data-testid="join-provider-cta"
              >
                Start earning today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <p className="mt-8 text-sm text-stone-500">
            Run a barbershop?{' '}
            <Link
              to="/for-barbers"
              className="text-[#D4AF37] hover:text-[#e8c547] transition-colors underline underline-offset-4 decoration-[#D4AF37]/40"
              data-testid="for-barbers-link"
            >
              We built a flow just for you
            </Link>
          </p>
        </div>
      </section>

      {/* Regional Directory, 84 SEO landing pages crawl surface */}
      <RegionalDirectoryFooter />

      <Footer />
    </div>
  );
}
